import { useCallback, type JSX } from "react";

import { ErrorPage } from "@mobsuccess-devops/react-ui/ErrorPage";
import { useRouter, type ErrorComponentProps } from "@tanstack/react-router";

import { isAuthFailure } from "../../public/auth/auth-failure";
import { NotFoundError, ForbiddenError } from "../../public/errors";
import { getHttpErrorStatus } from "../../public/tanstack-query/http-error";

import { SessionRecoveryFallback } from "./SessionRecoveryFallback";

export function ErrorComponent({ error }: ErrorComponentProps): JSX.Element {
  const router = useRouter();
  // `reset` only clears a render error; a loader error would rethrow on the
  // spot. Invalidating replaces every match, which resets the boundary and
  // re-runs the failed loaders as well. Kept stable: the fallback re-runs the
  // recovery whenever `retry` changes, and this component re-renders on every
  // router store update.
  const retrySession = useCallback(() => {
    void router.invalidate();
  }, [router]);
  if (error instanceof ForbiddenError) {
    return <ErrorPage.NotAllowed />;
  }
  if (error instanceof NotFoundError) {
    return <ErrorPage.DataNotFound />;
  }
  // An entity deleted while its route is mounted (or a stale bookmark) reaches
  // the boundary as a query error wrapping a 404: missing data, not a crash.
  const status = getHttpErrorStatus(error);
  if (status === 404) {
    return <ErrorPage.DataNotFound />;
  }
  if (status === 403) {
    return <ErrorPage.NotAllowed />;
  }
  if (isAuthFailure(error)) {
    return <SessionRecoveryFallback retry={retrySession} />;
  }
  return <ErrorPage.Any />;
}
